import clsx from 'clsx'
import { ChevronDownIcon } from 'lucide-react'
import React, { forwardRef } from 'react'

interface FilterPillButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'className' | 'value'
  > {
  open?: boolean;
  label: React.ReactNode;
  count: number;
  hideChevron?: boolean;
}

export const FilterPillButton = forwardRef<
  HTMLButtonElement,
  FilterPillButtonProps
>(({ open, label, count, disabled, hideChevron, ...props }, forwardedRef) => {
  function getOpenState () {
    // @ts-expect-error: `data-state` is injected by popover components but
    // not explicitly typed
    if (open === undefined) return props['data-state']

    return open ? 'open' : 'closed'
  }

  const hasValue = count > 0

  return (
    <button
      {...props}
      className={clsx(
        'group/filter-pill font-normal appearance-none flex gap-1 items-center rounded-full body-sm text-gray-800 border border-gray-200 transition py-2 outline outline-2 outline-offset-2 outline-transparent text-nowrap disabled: focus-visible:bg-gray-200 focus-visible:outline-thatch-500 ',
        {
          'pl-3 pr-2': hasValue,
          'pl-4 pr-2': !hasValue,
          'pr-3': hideChevron && hasValue,
          'pr-4': hideChevron && !hasValue,
          'text-gray-800/40 bg-gray-100 border-gray-200 pointer-events-none': disabled,
          'font-medium border-gray-400': getOpenState() === 'open',
          'border-thatch-500 bg-thatch-200 hover:border-thatch-600 hover:bg-thatch-300': hasValue && !disabled,
          'bg-gray-100 data-[state*="open"]:bg-gray-200 hover:bg-gray-200 hover:border-gray-400': !hasValue && !disabled,
          'hover:bg-gray-100 hover:border-gray-200': !hasValue && disabled,
        }
      )}
      data-state={getOpenState()}
      ref={forwardedRef}
      type='button'
    >
      {label}
      {hasValue
        ? (
          <span
            className={clsx('w-4 h-4 rounded-full text-white caption flex items-center justify-center bg-gray-300 leading-none', {
              'bg-thatch-500 group-hover/filter-pill:bg-thatch-600': !disabled,
            })}
            aria-label={`${count} selected`}
          >
            {count}
          </span>
          )
        : null}
      <span
        className='text-gray-500 group-disabled/filter-pill:text-[currentColor] group-data-[state*="open"]/filter-pill:rotate-180 shrink-0'
        hidden={hideChevron}
      >
        <ChevronDownIcon aria-hidden size={12} />
      </span>
    </button>
  )
})
